import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { Button, InlineMessages, InlineMessagesType, Types } from '@marriott/mi-ui-library';
import {
  AddressType,
  CreateOrderForGroupBookingUsingEmailLinkDttResponse,
  PropertyAcceptedPaymentCardsResponse,
  getPropertyAcceptedPaymentCardsQuery,
} from '@marriott/mi-groups-graphql';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { AddressInfo, PaymentInfoIframe, UserInfo } from '../../molecules';
import { useCustomerInfo } from '../../hooks';
import { getAddress, getPaymentMethodId, getPreferredPaymentInput, getPreferredPaymentDttInput } from '../../utils';
import { useDttStore, useLocaleStore } from '../../store';
import { PreferredPaymentFormData, PreferredPaymentInfoProps } from './PreferredPaymentInfo.types';
import {
  StyledPreferredPaymentInfo,
  StyledPreferredPaymentForm,
  StyledCtaContainer,
} from './PreferredPaymentInfo.styles';
import { CustomDomContentLoaded } from '../../components/CustomDomContentLoaded/CustomDomContentLoaded';
import { CreditCardType, PaymentInfoFormData } from '../PaymentInfo/PaymentInfo.types';
import { useCreateOrderForGroupBooking } from './PreferredPaymentInfo.transformer';

export const PreferredPaymentInfoConfig = {
  emptyLabel: 'PreferredPaymentInfo',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/rfp/preferredpayment`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PreferredPaymentInfo: FC<{ model: PreferredPaymentInfoProps }> = (props: any) => {
  const { userInfo, userAddress, paymentInfo, submitCta, apiError } = props.model;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const router = useRouter();
  const queryParams = router.query as Record<string, string>;

  const { locale } = useLocaleStore();
  const { isDtt } = useDttStore();

  const [paymentSectionData, setPaymentSectionData] = useState<PaymentInfoFormData>({
    paymentInfo: {
      cardType: '',
      cardHolderName: '',
      cardNumber: '',
      expirationMonth: {
        label: '',
        value: '',
      },
      expirationYear: {
        label: '',
        value: '',
      },
      cvvNumber: '',
      savedCardId: '',
      tokenizedPAN: '',
      panLast4: '',
      isSavedCard: false,
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const { customerInfo, customerPaymentMethods } = useCustomerInfo(true);

  const [getPropertyAcceptedPaymentCards, { data: propertyAcceptedPaymentCards }] = useLazyQuery<{
    property: PropertyAcceptedPaymentCardsResponse;
  }>(getPropertyAcceptedPaymentCardsQuery);

  const { acceptedPaymentMethods } = propertyAcceptedPaymentCards?.property || {};

  const {
    control,
    getValues,
    setValue,
    resetField,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<PreferredPaymentFormData>({
    defaultValues: {
      userInfo: {
        firstName: '',
        lastName: '',
        email: queryParams['CustEmAdd'],
        callingCode: {
          code: '',
          label: '',
          description: '',
          value: '',
          shortDescription: '',
        },
        phoneNumber: '',
      },
      addressInfo: {
        addressType: AddressType.BUSINESS,
        companyName: '',
        addressLine1: '',
        addressLine2: '',
        country: {
          code: '',
          label: '',
          description: '',
          value: '',
        },
        city: '',
        state: '',
        zipcode: '',
      },
    },
  });

  useEffect(() => {
    const uxlOptions: LazyQueryHookOptions = {
      variables: {
        propertyId: queryParams['PropCode'],
      },
      context: {
        headers: {
          'accept-language': locale,
        },
      },
      fetchPolicy: 'network-only',
    };
    getPropertyAcceptedPaymentCards(uxlOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (customerInfo?.userInfo) {
      setValue('userInfo.firstName', customerInfo.userInfo.firstName);
      setValue('userInfo.lastName', customerInfo.userInfo.lastName);
      setValue('userInfo.email', customerInfo.userInfo.email);
      setValue('userInfo.phoneNumber', customerInfo.userInfo.phoneNumber);
      customerInfo.userInfo.callingCode.description &&
        setValue('userInfo.callingCode', customerInfo.userInfo.callingCode);
    }
    customerInfo?.addressInfo && resetField('addressInfo', { defaultValue: customerInfo.addressInfo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo]);

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  const [createOrderForGroupBooking] = useCreateOrderForGroupBooking();

  const handleSubmitPreferredPayment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(false);

    const { paymentMethodId, errors } = await getPaymentMethodId(
      paymentSectionData,
      queryParams['PropCode'],
      isDtt && queryParams['IsAcrs']?.toLowerCase() === 'true'
    );
    setApiErrors(errors);

    const isContactInfoValid = await trigger();

    // utilized iframe window properties to trigger error of child-paymentinfo form
    const paymentSectionTrigger = iframeRef.current?.contentWindow?.paymentSectionTrigger;
    const isPaymentInfoValid = paymentSectionTrigger ? await paymentSectionTrigger() : false;

    // if no input errors, proceed with submit
    if (isContactInfoValid && isPaymentInfoValid && !errors?.length) {
      setLoading(true);
      const preferredPaymentFormData = getValues();
      if (paymentMethodId) {
        const preferredPaymentInput = isDtt
          ? getPreferredPaymentDttInput(preferredPaymentFormData, queryParams, paymentMethodId)
          : getPreferredPaymentInput(preferredPaymentFormData, queryParams, paymentMethodId);

        createOrderForGroupBooking({
          variables: { input: preferredPaymentInput },
          context: {
            headers: {
              'accept-language': locale,
            },
          },
          fetchPolicy: 'no-cache',
          onCompleted: response => {
            const data = response.sales.groupSales
              .submitCreditCardEmailPayment as CreateOrderForGroupBookingUsingEmailLinkDttResponse;

            if (data?.basicInformation?.externalIdInfo?.[0]?.id) {
              const {
                basicInformation,
                contactInformation: { address },
              } = data.property;

              const propertyAddress = getAddress(
                address?.line1 ?? '',
                address?.city ?? '',
                address?.stateProvince?.description ?? '',
                address?.country?.description ?? '',
                address?.postalCode ?? ''
              );

              const searchParams = new URLSearchParams();
              searchParams.append('brandCode', basicInformation.brand.id.toLowerCase());
              searchParams.append('propertyName', basicInformation.name);
              searchParams.append('propertyAddress', propertyAddress);
              searchParams.append('customerEmail', preferredPaymentFormData.userInfo.email);
              searchParams.append('cardType', paymentSectionData.paymentInfo.cardType || '');

              window.location.href = `${submitCta.paymentConfirmationUrl}?${searchParams.toString()}`;
            } else {
              handleError();
              window.scrollTo(0, 0);
            }
          },
          onError: () => {
            handleError();
            window.scrollTo(0, 0);
          },
        });
      } else {
        handleError();
      }
    }
  };

  return (
    <EditableComponent config={PreferredPaymentInfoConfig} {...props}>
      <CustomDomContentLoaded>
        <StyledPreferredPaymentInfo
          data-testid="preferredpayment"
          data-component-name="o-groups-preferredpayment"
          className="fullbleed m-container-fullbleed"
        >
          <div className="container pb-3 px-0">
            {error ? (
              <div className="mb-4">
                <InlineMessages type={InlineMessagesType.Error} title={apiError} severity="1" />
              </div>
            ) : null}
            <form onSubmit={handleSubmitPreferredPayment}>
              <StyledPreferredPaymentForm>
                <UserInfo
                  labels={userInfo}
                  customerCallingCountry={customerInfo?.userInfo.callingCode.shortDescription}
                  isEmailDisabled={true}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  clearErrors={clearErrors}
                />
                <AddressInfo
                  labels={userAddress}
                  customerAddressType={customerInfo?.addressInfo.addressType}
                  customerCountry={customerInfo?.addressInfo.country.value}
                  customerState={customerInfo?.addressInfo.state}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  clearErrors={clearErrors}
                />
                <PaymentInfoIframe
                  {...paymentInfo}
                  iframeRef={iframeRef}
                  acceptedCreditCards={acceptedPaymentMethods?.creditcards}
                  customerPaymentMethods={customerPaymentMethods}
                  apiErrors={apiErrors}
                  setPaymentSectionData={setPaymentSectionData}
                />
              </StyledPreferredPaymentForm>
              <StyledCtaContainer>
                <Button
                  type={Types.ButtonTypeVariation.Submit}
                  ariaLabel={submitCta.ariaLabel}
                  className={clsx('m-button-primary m-button-m submit-cta', loading && 'skeleton-loader')}
                  isDisabled={loading}
                >
                  {submitCta.label}
                </Button>
              </StyledCtaContainer>
            </form>
          </div>
        </StyledPreferredPaymentInfo>
      </CustomDomContentLoaded>
    </EditableComponent>
  );
};
